import {
  isCatalystInsight,
  isEarningsInsight,
  isInsightFilter,
} from '@api/api/insights';
import { GroupedInsights } from '@chart';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import {
  InsightIconsRoot,
  InsightIconsRootProps,
} from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '@terminal/components/insights-tooltip/InsightsTooltip';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import insightIconStyles from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-icon/InsightIcon.module.scss';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import { CatalystInsightIcon } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';
import clsx from 'clsx';
import { isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './ChartInsightIcon.module.scss';
import { calculateIconPosition } from './utils';

interface ChartInsightIconProps {
  groupedInsights: GroupedInsights;
  chartWidth: number;
  insightIconRight: number;
  insightIconTop: number;
  yOffsetCount: number;
  minIconY?: number;
  isSimpleChart?: boolean;
  source: PosthogTrackingSource;
  disableClick?: boolean;
}

export const ChartInsightIcon = ({
  groupedInsights,
  chartWidth,
  insightIconTop,
  insightIconRight,
  yOffsetCount,
  minIconY,
  isSimpleChart,
  source,
  disableClick = false,
}: ChartInsightIconProps) => {
  const { t } = useTranslation(['chart']);
  const { openDrawer: openInsightDrawer } = useInsightDrawer();

  const singleIconWidth = isSimpleChart ? 12 : 24;
  const singleIconHeight = isSimpleChart ? 14 : 28;

  const verticalCombinedWidth = isSimpleChart ? 12 : 24;
  const verticalCombinedHeight = isSimpleChart ? 24 : 47;

  return (
    <>
      {groupedInsights.insights.map((insight, idx) => {
        if (isCatalystInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            chartWidth,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
            minIconY,
          });

          return (
            <div
              key={insight.id}
              className={clsx(styles.iconContainer, {
                [styles.notClickable]: disableClick,
              })}
              style={{ top: iconY, right: iconRight }}
            >
              <CatalystInsightIcon
                catalystInsightId={insight.id}
                subCategory={insight.sub_category}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                identifier={insight.identifier}
                source={source}
                category={insight.category}
                isClickable={!disableClick}
              />
            </div>
          );
        }

        if (isEarningsInsight(insight)) {
          const { iconY, iconRight } = calculateIconPosition({
            idx,
            chartWidth,
            iconHeight: singleIconHeight,
            iconWidth: singleIconWidth,
            insightIconTop,
            insightIconRight,
            yOffsetCount,
            minIconY,
          });

          return (
            <div
              key={insight.id}
              className={clsx(styles.iconContainer, {
                [styles.notClickable]: disableClick,
              })}
              style={{ top: iconY, right: iconRight }}
            >
              <EarningsInsightIcon
                entityTag={insight.entity_tag}
                earningsInsight={insight}
                customSize={{
                  width: singleIconWidth,
                  height: singleIconHeight,
                }}
                isBordered
                source={source}
                isClickable={!disableClick}
              />
            </div>
          );
        }

        if (!isInsightFilter(insight)) {
          return null;
        }

        const isStarred = isToday(new Date(insight.created_at));
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insight.metadata.conditions,
        });
        const isCombined = conditions.length > 1;
        const iconWidth = isCombined ? verticalCombinedWidth : singleIconWidth;
        const iconHeight = isCombined
          ? verticalCombinedHeight
          : singleIconHeight;

        const { iconY, iconRight } = calculateIconPosition({
          idx,
          chartWidth,
          iconHeight,
          iconWidth,
          insightIconTop,
          insightIconRight,
          yOffsetCount,
          minIconY,
        });

        const iconSize = {
          width: `${iconWidth}px`,
          height: `${iconHeight}px`,
        };

        const episodeLabel = t('chart:insightCard.episode', {
          count: insight.metadata.episode_count,
        });

        const handleScenarioClick = disableClick
          ? undefined
          : () => {
              openInsightDrawer({
                id: insight.id,
                entityTag: insight.metadata.entity,
                trackingProperties: { source },
              });
            };

        const insightTooltipProps: InsightIconsRootProps = {
          conditions,
          direction: getExploreDirection(insight.metadata.direction),
          customSize: iconSize,
          isStarred,
          isCombined,
          layout: 'vertical',
          isSparklesCondensed: isSimpleChart,
        };
        const insightInteractionProps = handleScenarioClick
          ? {
              onIconClick: handleScenarioClick,
              source,
              insightId: insight.id,
              entities: insight.metadata.entity
                ? [insight.metadata.entity]
                : undefined,
            }
          : {};

        return (
          <InsightsTooltip
            key={insight.id}
            horizon={insight.metadata.horizon}
            median={insight.metadata.median_return}
            stars={insight.metadata.stars}
            name={insight.card.name}
            entityTag={insight.metadata.entity}
            episodeLabel={episodeLabel}
            {...insightTooltipProps}
          >
            <div
              key={insight.id}
              className={clsx(styles.iconContainer, {
                [styles.notClickable]: disableClick,
              })}
              style={{ top: iconY, right: iconRight }}
            >
              <div className={insightIconStyles.scenarioIconWrapper}>
                <InsightIconsRoot
                  {...insightTooltipProps}
                  {...insightInteractionProps}
                />
              </div>
            </div>
          </InsightsTooltip>
        );
      })}
    </>
  );
};
